/**
 * Asset export configuration
 *
 * USAGE:
 * ```typescript
 * const config = useMemo(() =>
 *   createAssetExportConfig(sourceOptions),
 *   [sourceOptions]
 * );
 *
 * <UniversalExportModal config={config} />
 * ```
 */

import {
  ClockIcon,
  NoSymbolIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  Frozen as FrozenIcon,
  Start as StarIcon,
} from '@/components/chariot-ui';
import type {
  ExportFilterSection,
  UniversalExportConfig,
} from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import { XCircleIcon } from '@/components/icons';
import type { StatusChipVariant } from '@/components/legacy-ui/StatusChip';
import type { Asset, Filter, Operator } from '@/types';
import { ASSET_STATUS_OPTIONS } from '@/utils/mappings.util';
import { assetStatusVariants } from '@/utils/statusChip.util';

/**
 * Interface for source options (from integrations)
 * Supports both flat options and grouped options (e.g., Chariot group)
 */
export interface SourceOption {
  readonly label: string;
  readonly value: string;
  readonly isGroup?: boolean;
  readonly items?: readonly SourceOption[];
}

/**
 * Icon map for asset statuses (matches AssetStatus.tsx patterns)
 */
const ASSET_STATUS_ICONS: Record<string, React.ReactNode> = {
  active: React.createElement(StarIcon, { className: 'size-3 shrink-0' }),
  pending: React.createElement(ClockIcon, { className: 'size-3 shrink-0' }),
  inactive: React.createElement(NoSymbolIcon, {
    className: 'size-3 shrink-0 stroke-[2px]',
  }),
  expiring: React.createElement(XCircleIcon, { className: 'size-3 shrink-0' }),
  frozen: React.createElement(FrozenIcon, { className: 'size-3 shrink-0' }),
  deleted: React.createElement(TrashIcon, { className: 'size-3 shrink-0' }),
};

/**
 * Column definitions - 15 columns total
 * Based on ExportAssets.tsx lines 21-44
 * Added: attack_surface, tags, country, asset_tuple
 */
export const ASSET_COLUMNS = [
  { id: 'asset_tuple', label: 'Asset Tuple', defaultSelected: true },
  { id: 'asset_name', label: 'Asset Name', defaultSelected: true },
  { id: 'asset_identifier', label: 'Asset Identifier', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'origins', label: 'Origins', defaultSelected: true },
  { id: 'parent', label: 'Discovered Via', defaultSelected: true },
  { id: 'attack_surface', label: 'Attack Surface', defaultSelected: true },
  { id: 'tags', label: 'Tags', defaultSelected: true },
  { id: 'country', label: 'Country', defaultSelected: true },
  { id: 'as_name', label: 'AS Name', defaultSelected: true },
  { id: 'as_number', label: 'AS Number', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
  { id: 'notes', label: 'Notes', defaultSelected: true },
  { id: 'first_seen', label: 'First Seen', defaultSelected: true },
  { id: 'last_seen', label: 'Last Seen', defaultSelected: true },
] as const;

/**
 * Extract class values from source options
 * Handles both grouped options (e.g., Chariot with items) and flat options
 *
 * @param options - Selected source options (may include groups)
 * @returns Array of class values for relationship query
 */
function extractClassValues(
  options: readonly Readonly<{
    value: string;
    isGroup?: boolean;
    items?: readonly { value: string }[];
  }>[]
): string[] {
  return options
    .flatMap(option => {
      if (option.isGroup && option.items) {
        return option.items.map(item => item.value);
      }
      return option.value;
    })
    .filter(value => value && typeof value === 'string' && value.trim() !== '');
}

/**
 * Convert a UI asset status code to the composite filter the backend expects.
 * "Active", "Inactive", and "Expiring" are all status=A in the DB, differentiated
 * by the `visited` timestamp. The backend resolves relative dates like "now-48h".
 */
function assetStatusToFilter(status: string): Filter | null {
  switch (status) {
    case 'A': // Active: visited within 48h
      return {
        field: '',
        operator: 'AND',
        value: [
          { field: 'status', operator: '=', value: 'A' },
          { field: 'visited', operator: '>=', value: 'now-48h' },
        ],
      };
    case 'U': // Inactive: visited between 48h and 7d ago
      return {
        field: '',
        operator: 'AND',
        value: [
          { field: 'status', operator: '=', value: 'A' },
          { field: 'visited', operator: '<', value: 'now-48h' },
          { field: 'visited', operator: '>=', value: 'now-7d' },
        ],
      };
    case 'I': // Expiring: visited more than 7d ago
      return {
        field: '',
        operator: 'AND',
        value: [
          { field: 'status', operator: '=', value: 'A' },
          { field: 'visited', operator: '<', value: 'now-7d' },
        ],
      };
    case 'P':
      return { field: 'status', operator: '=', value: 'P' };
    case 'F':
      return { field: 'status', operator: 'STARTS WITH', value: 'F' };
    case 'D':
      return { field: 'status', operator: '=', value: 'D' };
    default:
      return null;
  }
}

/**
 * Base asset export configuration (without source filter)
 * Source filter is injected dynamically via factory function
 */
export const assetExportConfigBase = {
  entityType: 'Asset',
  apiEntityType: 'asset' as const,
  storageKey: 'ADVANCE_EXPORT_OPTIONS.assets.v4',

  filterSections: [
    {
      id: 'status',
      label: 'Status',
      options: ASSET_STATUS_OPTIONS.map(({ label, value, id }) => ({
        label,
        value,
        variant: assetStatusVariants[id] as StatusChipVariant,
        icon: ASSET_STATUS_ICONS[id],
      })),
      defaultSelected: [
        {
          label: 'Active',
          value: 'A',
          variant: assetStatusVariants['active'] as StatusChipVariant,
          icon: ASSET_STATUS_ICONS['active'],
        },
      ], // Default: Active only
      showWhenSelected: false,
    },
  ],

  columns: [...ASSET_COLUMNS],

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
    { label: 'PDF', value: 'pdf' },
  ],
  defaultFormat: { label: 'CSV', value: 'csv' },

  buildQuery: (
    filters: Readonly<
      Record<
        string,
        readonly Readonly<{
          value: string;
          isGroup?: boolean;
          items?: readonly { value: string }[];
        }>[]
      >
    >,
    _selectedRows: readonly string[]
  ) => {
    let builder = GraphQueryBuilder.create(['Asset']);

    if (filters.status && filters.status.length > 0) {
      const statusFilters = filters.status
        .map(s => assetStatusToFilter(s.value as string))
        .filter(Boolean) as Filter[];

      if (statusFilters.length === 1) {
        builder = builder.filters(statusFilters);
      } else if (statusFilters.length > 0) {
        builder = builder.orFilters(statusFilters);
      }
    }

    if (filters.origins && filters.origins.length > 0) {
      const originValues = extractClassValues(filters.origins);

      if (originValues.length > 0) {
        builder = builder.filters([
          {
            field: '',
            operator: 'OR',
            value: originValues.map(origin => ({
              field: 'origins',
              operator: 'IN',
              value: origin,
              reverseOperands: true,
            })),
          },
        ] as Filter[]);
      }
    }

    return builder.orderBy('name', false).build();
  },

  validateExport: (
    _filters: Readonly<Record<string, readonly Readonly<unknown>[]>>,
    columns: readonly Readonly<unknown>[],
    _selectedRows: readonly string[]
  ) => {
    if (columns.length === 0) {
      return [
        {
          field: 'columns' as const,
          message: 'Please select at least one column to export.',
        },
      ];
    }
    return null;
  },
} as const;

/**
 * Factory function for creating complete asset config with dynamic source filter
 *
 * This function is called in the parent component with the current sourceOptions
 * to inject the dynamic source filter section.
 *
 * @param sourceOptions - Available source options from integrations (built in parent component)
 * @returns Complete asset export config with dynamic source filter
 *
 * @example
 * ```typescript
 * // In parent component:
 * const sourceOptions = useMemo(() => {
 *   // Build source options from integrations
 *   return [
 *     { label: 'Chariot', value: 'chariot', isGroup: true, items: [...] },
 *     { label: 'GitHub', value: 'github' },
 *     // ...
 *   ];
 * }, [integrations]);
 *
 * const assetExportConfig = useMemo(() => {
 *   return createAssetExportConfig(sourceOptions);
 * }, [sourceOptions]);
 * ```
 */
export function createAssetExportConfig(
  sourceOptions: readonly SourceOption[]
): UniversalExportConfig<Asset> {
  // Create source filter section from dynamic options
  const sourceFilterSection: ExportFilterSection = {
    id: 'origins',
    label: 'Origins',
    options: sourceOptions as readonly Readonly<{
      label: string;
      value: string;
    }>[],
    defaultSelected: [],
    showWhenSelected: false,
  };

  return {
    ...assetExportConfigBase,
    storageKey: 'ADVANCE_EXPORT_OPTIONS.assets.v5',
    filterSections: [
      ...assetExportConfigBase.filterSections,
      sourceFilterSection,
    ],
  } as UniversalExportConfig<Asset>;
}
